/**
 * Announcements API Service
 * Manages instructor announcements and broadcasts
 */

import { sessionManagerApi, labManagerApi } from './apiClient';
import { mockFallback } from '../lib/mockFallback';
import type { Announcement, CreateAnnouncementInput, PaginatedResponse, Cohort, Lab } from '../types';

interface AnnouncementListParams {
  page?: number;
  limit?: number;
  type?: 'info' | 'warning' | 'success' | 'urgent';
  targetType?: 'all' | 'cohort' | 'lab';
}

// =====================================================
// BACKEND CONTRACT (session-manager /instructor/announcements)
// =====================================================
//
// GET  → { announcements: BackendAnnouncement[], total } (instructor.ts:115)
//   items: { id, title, body, createdAt, audience, isPinned, type, author,
//            readCount, totalRecipients }
// The list route does no server-side type/audience filtering, so the page's
// filters are applied here after mapping into the UI's Announcement shape.
interface BackendAnnouncement {
  id: string;
  title: string;
  body: string;
  createdAt: string;
  audience?: string;
  isPinned?: boolean;
  type?: 'info' | 'warning' | 'success' | 'urgent';
  author?: string;
  readCount?: number;
  totalRecipients?: number;
}

/** recipients_query shapes accepted by the broadcast endpoint (MessagingController). */
type RecipientsQuery =
  | { kind: 'all' }
  | { kind: 'cohort' | 'vertical' | 'region'; targetId: string };

function mapAnnouncement(a: BackendAnnouncement): Announcement {
  // `audience` maps onto targetType where the UI has an equivalent; anything
  // else (e.g. vertical/region broadcasts) reads as "all" for display.
  const targetType: Announcement['targetType'] =
    a.audience === 'cohort' || a.audience === 'lab' ? a.audience : 'all';
  return {
    id: a.id,
    title: a.title,
    message: a.body,
    type: a.type ?? 'info',
    targetType,
    authorId: '',
    authorName: a.author ?? 'Instructor',
    isPinned: !!a.isPinned,
    readCount: a.readCount ?? 0,
    totalRecipients: a.totalRecipients ?? 0,
    createdAt:
      typeof a.createdAt === 'string' ? a.createdAt : new Date(a.createdAt).toISOString(),
  };
}

/**
 * Get announcements list
 */
export async function getAnnouncements(params: AnnouncementListParams = {}): Promise<PaginatedResponse<Announcement>> {
  try {
    const response = await sessionManagerApi.get('/instructor/announcements', { params });
    const backend = (response.data?.announcements ?? []) as BackendAnnouncement[];
    let items = backend.map(mapAnnouncement);
    if (params.type) {
      items = items.filter((a) => a.type === params.type);
    }
    if (params.targetType) {
      items = items.filter((a) => a.targetType === params.targetType);
    }
    const limit = params.limit || 20;
    return {
      data: items,
      pagination: {
        page: params.page || 1,
        limit,
        total: typeof response.data?.total === 'number' ? response.data.total : items.length,
        totalPages: Math.max(1, Math.ceil(items.length / limit)),
      },
    };
  } catch (error) {
    console.error('Failed to fetch announcements:', error);
    // Return mock data
    const mockAnnouncements: Announcement[] = [
      {
        id: 'a1',
        title: 'Platform Maintenance Scheduled',
        message: 'The platform will be undergoing maintenance on Saturday from 2am-4am EST. Please save your work.',
        type: 'warning',
        targetType: 'all',
        authorId: 'instructor1',
        authorName: 'Dr. Smith',
        isPinned: true,
        readCount: 45,
        totalRecipients: 156,
        createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24).toISOString(),
      },
      {
        id: 'a2',
        title: 'New Lab Available: Multi-Agent Systems',
        message: 'We\'ve just released a new advanced lab on building multi-agent systems. Check it out!',
        type: 'success',
        targetType: 'all',
        authorId: 'instructor1',
        authorName: 'Dr. Smith',
        isPinned: false,
        readCount: 78,
        totalRecipients: 156,
        createdAt: new Date(Date.now() - 1000 * 60 * 60 * 48).toISOString(),
      },
      {
        id: 'a3',
        title: 'Deadline Reminder: RAG Lab',
        message: 'The RAG Applications lab is due this Friday. Please submit your work on time.',
        type: 'info',
        targetType: 'cohort',
        targetId: 'cohort-1',
        targetName: 'Spring 2024 Cohort',
        authorId: 'instructor1',
        authorName: 'Dr. Smith',
        isPinned: false,
        readCount: 22,
        totalRecipients: 35,
        createdAt: new Date(Date.now() - 1000 * 60 * 60 * 12).toISOString(),
      },
      {
        id: 'a4',
        title: 'Office Hours Update',
        message: 'Office hours have been moved to Thursdays 3-5pm this week only.',
        type: 'info',
        targetType: 'all',
        authorId: 'instructor2',
        authorName: 'Prof. Johnson',
        isPinned: false,
        readCount: 89,
        totalRecipients: 156,
        createdAt: new Date(Date.now() - 1000 * 60 * 60 * 72).toISOString(),
      },
    ];

    let filtered = mockAnnouncements;
    if (params.type) {
      filtered = filtered.filter((a) => a.type === params.type);
    }
    if (params.targetType) {
      filtered = filtered.filter((a) => a.targetType === params.targetType);
    }

    const mock = {
      data: filtered,
      pagination: {
        page: params.page || 1,
        limit: params.limit || 20,
        total: filtered.length,
        totalPages: Math.ceil(filtered.length / (params.limit || 20)),
      },
    };
    return mockFallback(error, mock);
  }
}

/**
 * Derive the broadcast `recipients_query` from the page's target selection.
 * The `POST /instructor/announcements` route that actually handles this is
 * MessagingController.createAnnouncement (mounted first, so it shadows the
 * legacy instructor.ts handler); it only understands all/cohort/vertical/region.
 */
function recipientsQueryFromInput(data: CreateAnnouncementInput): RecipientsQuery {
  if (data.targetType === 'cohort') {
    if (!data.targetId) {
      throw new Error('Select a cohort before sending this announcement.');
    }
    return { kind: 'cohort', targetId: data.targetId };
  }
  if (data.targetType === 'lab') {
    // The broadcast endpoint has no lab audience; fail loudly rather than
    // silently fanning out to everyone.
    throw new Error(
      'Lab-targeted announcements are not supported yet — choose "All Students" or a specific cohort.',
    );
  }
  return { kind: 'all' };
}

/**
 * Create a new announcement.
 *
 * Sends the shape MessagingController.createAnnouncement requires
 * (`{ subject, message_body, recipients_query }`) — the previous
 * `{ title, message, ... }` payload 400'd with `missing_fields`. Response is
 * `{ announcement_id, recipient_count }`.
 */
export async function createAnnouncement(data: CreateAnnouncementInput): Promise<Announcement> {
  const recipients_query = recipientsQueryFromInput(data);
  const response = await sessionManagerApi.post('/instructor/announcements', {
    subject: data.title,
    message_body: data.message,
    recipients_query,
    send_test_to_me_first: false,
  });
  const recipientCount =
    typeof response.data?.recipient_count === 'number' ? response.data.recipient_count : 0;
  return {
    id: response.data?.announcement_id ?? `a-${Date.now()}`,
    title: data.title,
    message: data.message,
    type: data.type,
    targetType: data.targetType,
    targetId: data.targetId,
    authorId: '',
    authorName: 'You',
    isPinned: data.isPinned ?? false,
    readCount: 0,
    totalRecipients: recipientCount,
    createdAt: new Date().toISOString(),
  };
}

/**
 * Update an announcement
 */
export async function updateAnnouncement(id: string, data: Partial<CreateAnnouncementInput>): Promise<Announcement> {
  const response = await sessionManagerApi.patch(`/instructor/announcements/${id}`, data);
  return response.data;
}

/**
 * Delete an announcement
 */
export async function deleteAnnouncement(id: string): Promise<void> {
  await sessionManagerApi.delete(`/instructor/announcements/${id}`);
}

/**
 * Pin/unpin an announcement
 */
export async function toggleAnnouncementPin(id: string, isPinned: boolean): Promise<Announcement> {
  const response = await sessionManagerApi.post(`/instructor/announcements/${id}/pin`, { isPinned });
  return response.data;
}

/**
 * Get cohorts for targeting announcements
 */
export async function getCohorts(): Promise<Cohort[]> {
  try {
    const response = await labManagerApi.get('/instructor/cohorts');
    return response.data.cohorts;
  } catch (error) {
    console.error('Failed to fetch cohorts:', error);
    const mock: Cohort[] = [
      {
        id: 'cohort-1',
        name: 'Spring 2024 Cohort',
        courseId: 'c1',
        courseName: 'LLM Fundamentals',
        status: 'active',
        startDate: '2024-01-15',
        endDate: '2024-05-15',
        maxCapacity: 40,
        enrolledCount: 35,
        completionRate: 45,
        averageProgress: 62,
        atRiskCount: 3,
      },
      {
        id: 'cohort-2',
        name: 'Advanced AI Winter 2024',
        courseId: 'c3',
        courseName: 'Advanced AI',
        status: 'active',
        startDate: '2024-02-01',
        endDate: '2024-06-01',
        maxCapacity: 30,
        enrolledCount: 28,
        completionRate: 30,
        averageProgress: 48,
        atRiskCount: 5,
      },
    ];
    return mockFallback(error, mock);
  }
}

/**
 * Get labs for targeting announcements
 */
export async function getLabsForAnnouncement(): Promise<Pick<Lab, 'id' | 'title'>[]> {
  try {
    const response = await labManagerApi.get('/instructor/labs', { params: { limit: 100 } });
    return response.data.data.map((lab: Lab) => ({ id: lab.id, title: lab.title }));
  } catch (error) {
    console.error('Failed to fetch labs:', error);
    const mock: Pick<Lab, 'id' | 'title'>[] = [
      { id: 'lab-1', title: 'Introduction to OpenAI API' },
      { id: 'lab-2', title: 'Building RAG Applications' },
      { id: 'lab-3', title: 'Fine-tuning Language Models' },
    ];
    return mockFallback(error, mock);
  }
}
